import React, { useState, useEffect } from "react";

const C = {
  bg: "#06080F", surface: "#0e1117", card: "#141820", border: "#1e2533",
  accent: "#F59E0B", accentDim: "#F59E0B22", text: "#F1F5F9", muted: "#64748B",
  danger: "#EF4444", success: "#22C55E", info: "#3B82F6", purple: "#8B5CF6",
};

function api(path, opts = {}) {
  return fetch(path, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  }).then(r => r.json());
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso)) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", border: `3px solid ${C.border}`, borderTopColor: C.accent, animation: "ds-spin 0.7s linear infinite" }} />
      <style>{"@keyframes ds-spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// Severity → color mapping
const SEV_COLOR = { critical: C.danger, high: C.accent, warning: C.info, ok: C.muted };

// Build flat grouped alert array from redZone data (one chip per alert type)
function buildAlerts(rz = {}) {
  const alerts = [];
  if ((rz.longHoldPayouts || []).length > 0)
    alerts.push({ id: "long-hold-payouts", severity: "critical", screen: "payouts",
      label: `${rz.longHoldPayouts.length} payout${rz.longHoldPayouts.length > 1 ? "s" : ""} on hold >48h` });
  if ((rz.urgentTicketCount || 0) + (rz.urgentCxTicketCount || 0) > 0)
    alerts.push({ id: "urgent-tickets", severity: "critical", screen: "tickets",
      label: `${(rz.urgentTicketCount || 0) + (rz.urgentCxTicketCount || 0)} urgent ticket(s) unresolved` });
  if ((rz.stalledSurveys || []).length > 0)
    alerts.push({ id: "stalled-surveys", severity: "high", screen: "surveys",
      label: `${rz.stalledSurveys.length} survey${rz.stalledSurveys.length > 1 ? "s" : ""} stalled at <20% fill` });
  if ((rz.staleReferralFlags || 0) > 0)
    alerts.push({ id: "stale-referrals", severity: "high", screen: "referrals",
      label: `${rz.staleReferralFlags} fraud flag${rz.staleReferralFlags > 1 ? "s" : ""} >72h old` });
  if ((rz.approvalBacklog || []).length > 0)
    alerts.push({ id: "approval-backlog", severity: "warning", screen: "surveys",
      label: `${rz.approvalBacklog.length} survey${rz.approvalBacklog.length > 1 ? "s" : ""} awaiting approval >24h` });
  if ((rz.qualityReviewPending || 0) > 0)
    alerts.push({ id: "quality-review", severity: "warning", screen: "participants",
      label: `${rz.qualityReviewPending} session${rz.qualityReviewPending > 1 ? "s" : ""} pending quality review` });
  return alerts;
}

// Build 4 queue card configs from queues data
function buildQueueCards(queues = {}, setScreen) {
  const p   = queues.participants || {};
  const s   = queues.surveys      || {};
  const f   = queues.finance      || {};
  const sup = queues.support      || {};
  const sev = (n, crit, high) => n >= crit ? "critical" : n >= high ? "high" : n > 0 ? "warning" : "ok";
  return [
    {
      title: "Participants", icon: "👥",
      rows: [
        { label: "Unverified",     count: p.unverified    || 0, sev: sev(p.unverified,    50, 20), onClick: () => setScreen("participants") },
        { label: "UPI unverified", count: p.upiUnverified || 0, sev: sev(p.upiUnverified, 20,  5), onClick: () => setScreen("participants") },
        { label: "Fraud flagged",  count: p.fraudFlagged  || 0, sev: sev(p.fraudFlagged,   1,  1), onClick: () => setScreen("referrals") },
      ],
    },
    {
      title: "Surveys", icon: "📋",
      rows: [
        { label: "Pending approval", count: s.pendingApproval || 0, sev: sev(s.pendingApproval, 3, 1), onClick: () => setScreen("surveys") },
        { label: "Underperforming",  count: s.underperforming || 0, sev: sev(s.underperforming, 5, 2), onClick: () => setScreen("surveys") },
        { label: "Ready to close",   count: s.readyToClose   || 0, sev: sev(s.readyToClose,   1, 1),   onClick: () => setScreen("surveys") },
      ],
    },
    {
      title: "Finance", icon: "💰",
      rows: [
        { label: "Under review",     count: f.underReview     || 0, sev: sev(f.underReview,     20, 5), onClick: () => setScreen("payouts") },
        { label: "On hold >48h",     count: f.longHold        || 0, sev: sev(f.longHold,          1, 1), onClick: () => setScreen("payouts") },
        { label: "Overdue invoices", count: f.overdueInvoices || 0, sev: sev(f.overdueInvoices,   3, 1), onClick: () => setScreen("customers") },
      ],
    },
    {
      title: "Support", icon: "🎫",
      rows: [
        { label: "P. Urgent",       count: sup.participantUrgent  || 0, sev: sev(sup.participantUrgent,  1, 1), onClick: () => setScreen("tickets") },
        { label: "C. Urgent",       count: sup.customerUrgent     || 0, sev: sev(sup.customerUrgent,     1, 1), onClick: () => setScreen("tickets") },
        { label: "SLA breached",    count: sup.slaBreached        || 0, sev: sev(sup.slaBreached,        3, 1), onClick: () => setScreen("tickets") },
        { label: "Unanswered (CX)", count: sup.unansweredCustomer || 0, sev: sev(sup.unansweredCustomer, 3, 1), onClick: () => setScreen("tickets") },
      ],
    },
  ];
}

// ── UI Components ─────────────────────────────────────────────────────────────

function TopIssueBar({ topIssue, setScreen }) {
  if (!topIssue) return null;
  const color = SEV_COLOR[topIssue.severity] || C.muted;
  const icon  = topIssue.severity === "critical" ? "🚨" : topIssue.severity === "high" ? "⚠️" : "ℹ️";
  return (
    <div style={{
      background: `${color}12`, border: `1px solid ${color}44`, borderRadius: 10,
      padding: "12px 16px", marginBottom: 12,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 1 }}>Top Priority</div>
          <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {topIssue.message}
          </div>
        </div>
      </div>
      <button
        onClick={() => setScreen(topIssue.screen)}
        style={{ background: color, color: "#fff", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
      >
        Review →
      </button>
    </div>
  );
}

function RedZoneStrip({ alerts, dismissed, onDismiss, setScreen }) {
  const visible = alerts.filter(a => !dismissed.has(a.id));
  if (!visible.length) return null;
  return (
    <div style={{
      background: "#EF444408", border: `1px solid ${C.danger}30`, borderRadius: 10,
      padding: "10px 14px", marginBottom: 20,
      display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Alerts</span>
      {visible.map(a => (
        <AlertChip key={a.id} alert={a} onDismiss={() => onDismiss(a.id)} onClick={() => setScreen(a.screen)} />
      ))}
    </div>
  );
}

function AlertChip({ alert, onDismiss, onClick }) {
  const color = SEV_COLOR[alert.severity] || C.muted;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 99, padding: "3px 8px 3px 10px" }}>
      <button onClick={onClick} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, fontSize: 12, padding: 0, fontFamily: "inherit" }}>
        {alert.label}
      </button>
      <button onClick={e => { e.stopPropagation(); onDismiss(); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, padding: "0 2px", lineHeight: 1, display: "flex", alignItems: "center" }}>
        ×
      </button>
    </div>
  );
}

function SignalTile({ label, primary, primarySub, trend, trendLabel, color, sub }) {
  const dir = trend != null ? (trend > 0 ? "up" : trend < 0 ? "down" : null) : null;
  const trendColor = dir === "up" ? C.success : dir === "down" ? C.danger : C.muted;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 22px" }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color: color || C.text, lineHeight: 1 }}>{primary ?? "—"}</div>
      {primarySub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{primarySub}</div>}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        {sub && <span style={{ fontSize: 11, color: C.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>}
        {dir && trendLabel && (
          <span style={{ fontSize: 11, color: trendColor, fontWeight: 700, flexShrink: 0 }}>
            {dir === "up" ? "↑" : "↓"} {trendLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function QueueCard({ title, icon, rows }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>{title}
      </div>
      <div>
        {rows.map((row, i) => <QueueRow key={i} {...row} last={i === rows.length - 1} />)}
      </div>
    </div>
  );
}

function QueueRow({ label, count, sev, onClick, last }) {
  const col = SEV_COLOR[sev] || C.muted;
  const clickable = count > 0 && !!onClick;
  return (
    <div
      onClick={clickable ? onClick : undefined}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 0", borderBottom: last ? "none" : `1px solid ${C.border}`,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <span style={{ fontSize: 13, color: C.text }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: col }}>{count}</span>
        {clickable && <span style={{ color: C.muted, fontSize: 12 }}>→</span>}
      </div>
    </div>
  );
}

const FEED_ICONS = {
  session_complete: "✅", session_abandon: "⚠️", register: "🆕",
  invitation_complete: "📋", report_generate: "📊",
  payout_under_review_to_pending_payout: "✅",
  payout_under_review_to_on_hold: "⏸️",
  payout_pending_payout_to_paid: "💸",
  payout_under_review_to_cancelled: "❌",
  milestone_1_reached: "🎯", milestone_2_reached: "🎯",
  fraud_flag_added: "🚩", status_changed: "🔄",
};

function ActivityFeed({ events }) {
  if (!events?.length)
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80, color: C.muted, fontSize: 13 }}>No activity in the last 24h</div>;
  return (
    <div style={{ overflowY: "auto", maxHeight: 320, scrollbarWidth: "none" }}>
      {events.map((e, i) => (
        <div key={i} style={{ display: "flex", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}`, alignItems: "flex-start" }}>
          <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{FEED_ICONS[e.eventType] || "•"}</span>
          <span style={{ fontSize: 13, color: C.text, flex: 1, lineHeight: 1.4 }}>{e.label}</span>
          <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginTop: 2 }}>{relativeTime(e.eventAt)}</span>
        </div>
      ))}
    </div>
  );
}

function SurveyRadar({ surveys, setScreen }) {
  if (!surveys?.length)
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80, color: C.muted, fontSize: 13 }}>No active surveys</div>;
  return (
    <div>
      {surveys.map(s => {
        const pct = Math.min(s.fillPct ?? 0, 100);
        const barColor = pct < 20 ? C.danger : pct < 50 ? C.accent : C.success;
        return (
          <div key={s.id} onClick={() => setScreen("surveys")} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.customerName || "—"} · {s.daysLive || 0}d · {s.modality}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: barColor }}>{s.fillPct ?? "—"}%</div>
                <div style={{ fontSize: 11, color: C.muted }}>{s.completions}/{s.targetSampleSize}</div>
              </div>
            </div>
            <div style={{ height: 4, background: C.border, borderRadius: 2 }}>
              <div style={{ height: "100%", background: barColor, borderRadius: 2, width: `${pct}%`, transition: "width 0.4s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ParticipantFunnel({ funnel }) {
  if (!funnel) return null;
  const stages = [
    { label: "Registered",   count: funnel.registered,      color: C.info },
    { label: "Verified",     count: funnel.verified,         color: C.purple },
    { label: "Profile Done", count: funnel.profileComplete,  color: C.accent },
    { label: "1st Survey",   count: funnel.firstSurveyDone,  color: "#34D399" },
    { label: "1st Payout",   count: funnel.firstPayoutMade,  color: C.success },
  ];
  const max = funnel.registered || 1;
  return (
    <div>
      {stages.map((stage, i) => {
        const pct = Math.round((stage.count || 0) / max * 100);
        const convPct = i > 0 && stages[i - 1].count > 0
          ? Math.round((stage.count || 0) / stages[i - 1].count * 100)
          : null;
        return (
          <div key={stage.label} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {i > 0 && <span style={{ fontSize: 11, color: C.muted }}>↓</span>}
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{stage.label}</span>
                {convPct != null && (
                  <span style={{ fontSize: 11, color: convPct < 50 ? C.danger : C.muted }}>({convPct}%)</span>
                )}
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: stage.color }}>
                {(stage.count || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div style={{ height: 5, background: C.border, borderRadius: 3 }}>
              <div style={{ height: "100%", background: stage.color, borderRadius: 3, width: `${pct}%`, transition: "width 0.4s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DashboardScreen({ setScreen }) {
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [dismissed, setDismissed] = useState(new Set());
  const [lastFetched, setLastFetched] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      api("/api/admin/stats")
        .then(d => {
          if (!active) return;
          // Avoid re-render when data is identical
          setStats(prev => JSON.stringify(prev) === JSON.stringify(d) ? prev : d);
          setLastFetched(new Date());
          setError("");
        })
        .catch(e => { if (active) setError(e.message); })
        .finally(() => { if (active) setLoading(false); });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const onDismiss = id => setDismissed(prev => new Set([...prev, id]));

  if (loading) return <Spinner />;
  if (error)   return <div style={{ color: C.danger, padding: 20, fontSize: 14 }}>{error}</div>;
  if (!stats)  return null;

  const alerts  = buildAlerts(stats.redZone);
  const qCards  = buildQueueCards(stats.queues, setScreen);
  const sl      = stats.signals          || {};
  const fillRate = sl.fillRate           || {};
  const payout  = sl.payoutLiability     || {};
  const dropOff = sl.dropOff24h          || {};
  const sq7d    = sl.sessionQuality7d    || {};
  const liabTotal = Object.values(payout.breakdown || {}).reduce((s, v) => s + (v.amountInr || 0), 0);
  const liabCount = Object.values(payout.breakdown || {}).reduce((s, v) => s + (v.count     || 0), 0);

  return (
    <div>
      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 900px) {
          .ds-signal-grid { grid-template-columns: repeat(2,1fr) !important; }
          .ds-middle-grid { grid-template-columns: 1fr !important; }
          .ds-bottom-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .ds-signal-grid { grid-template-columns: 1fr !important; }
          .ds-queue-grid  { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>Operations Dashboard</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
            {lastFetched ? `Updated ${relativeTime(lastFetched)} · refreshes every 60s` : "Loading…"}
          </div>
        </div>
      </div>

      {/* Top Issue — single highest-priority item shown prominently */}
      <TopIssueBar topIssue={stats.topIssue} setScreen={setScreen} />

      {/* Red Zone strip — remaining grouped alerts */}
      <RedZoneStrip alerts={alerts} dismissed={dismissed} onDismiss={onDismiss} setScreen={setScreen} />

      {/* Signal Tiles */}
      <div className="ds-signal-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 20 }}>
        <SignalTile
          label="Survey Fill Rate"
          primary={fillRate.avgPct != null ? `${fillRate.avgPct}%` : "—"}
          primarySub={`${fillRate.activeSurveyCount || 0} active survey${(fillRate.activeSurveyCount || 0) !== 1 ? "s" : ""}`}
          trend={fillRate.trend}
          trendLabel={fillRate.trend != null ? `${Math.abs(fillRate.trend)} vs prev 7d` : null}
          color={fillRate.avgPct == null ? C.text : fillRate.avgPct < 30 ? C.danger : fillRate.avgPct < 60 ? C.accent : C.success}
          sub="avg completion across active surveys"
        />
        <SignalTile
          label="Payout Liability"
          primary={liabTotal > 0 ? `₹${liabTotal.toLocaleString("en-IN")}` : "₹0"}
          primarySub={`${liabCount} payout${liabCount !== 1 ? "s" : ""} in pipeline`}
          color={liabTotal > 50000 ? C.accent : C.text}
          sub={`On hold: ₹${(payout.breakdown?.on_hold?.amountInr || 0).toLocaleString("en-IN")}`}
        />
        <SignalTile
          label="24h Registrations"
          primary={dropOff.registered24h ?? "—"}
          primarySub="new participants"
          trend={dropOff.verificationRate != null ? dropOff.verificationRate - 100 : null}
          trendLabel={dropOff.verificationRate != null ? `${dropOff.verificationRate}% verified (48h cohort)` : null}
          color={C.text}
          sub={dropOff.cohortBase ? `${dropOff.cohortVerified}/${dropOff.cohortBase} in cohort verified` : "no cohort yet"}
        />
        <SignalTile
          label="Session Quality · 7d"
          primary={sq7d.passRate != null ? `${sq7d.passRate}%` : "—"}
          primarySub={sq7d.total ? `${sq7d.total} sessions scored` : "no data"}
          color={sq7d.passRate == null ? C.text : sq7d.passRate < 70 ? C.danger : sq7d.passRate < 85 ? C.accent : C.success}
          sub={sq7d.inReview > 0 ? `${sq7d.inReview} need review` : sq7d.failing > 0 ? `${sq7d.failing} failed` : "all clear"}
        />
      </div>

      {/* Middle Row: Queues (2×2) + Activity Feed */}
      <div className="ds-middle-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div className="ds-queue-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {qCards.map(card => <QueueCard key={card.title} {...card} />)}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 14 }}>
            Activity Feed · last 24h
          </div>
          <ActivityFeed events={stats.activityFeed} />
        </div>
      </div>

      {/* Bottom Row: Survey Radar + Participant Funnel */}
      <div className="ds-bottom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 2 }}>Survey Performance</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>Active surveys · lowest fill first · click to open</div>
          <SurveyRadar surveys={stats.topSurveys} setScreen={setScreen} />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 2 }}>Participant Funnel</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 18 }}>Registration → first payout · conversion at each stage</div>
          <ParticipantFunnel funnel={stats.funnel} />
        </div>
      </div>
    </div>
  );
}
